import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  startGuest,
  signInWithGoogle,
  signUpWithEmail,
  REFERRAL_CODE_STORAGE_KEY,
} from '@/lib/auth-service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { TurnstileWidget, type TurnstileHandle } from '@/components/common/TurnstileWidget';
import AppStoreBadge from '@/components/common/AppStoreBadge';
import PlayStoreBadge from '@/components/common/PlayStoreBadge';
import PromoVideo from '@/components/common/PromoVideo';
import { PROMO_VIDEO_ID } from '@/lib/constants';
import { trackSignupPageView } from '@/lib/analytics';
import { shouldGateToApp } from '@/lib/platform';

/**
 * Guest-first entry (replaces the old email+password signup). Pick a username and
 * play immediately as an anonymous Firebase user; `startGuest` provisions the
 * profile + starter gift via POST /api/guest/start, gated by a managed Cloudflare
 * Turnstile token (the gift's abuse guard). Email/password only later, when the
 * player chooses to save their account from the dashboard.
 *
 * Exception: a visitor arriving via a friend's `?ref=CODE` link (captured into
 * sessionStorage by `App.tsx` before this page ever mounts, since referral
 * links point at `/`, not `/signup`) sees a real-account form instead: the
 * referral bonus only redeems on a non-anonymous account, so gambling on a
 * future guest conversion would lose most of these signups. They can still
 * opt into the guest flow via "Continue as guest instead", which leaves the
 * code in sessionStorage for a later dashboard conversion to redeem.
 *
 * Known gap (accepted 2026-07-20): a referred visitor on a phone is bounced to
 * /get-the-app by the install-store gate before the referral form renders, and
 * the native app has no code-entry surface, so their referral bonus is lost.
 * Revisit if mobile referral volume grows.
 */
export default function SignupPage() {
  const [refCode] = useState(() => sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY));
  const [showGuestForm, setShowGuestForm] = useState(false);
  // Phones/tablets can't sign up in the browser at all: the web game is
  // designed for big screens, so they go straight to the install-store page.
  const [gatedToApp] = useState(() => shouldGateToApp());

  // Signup-page visit = the paid click-through landing, the top of the campaign
  // funnel. Fire once on mount to GA4 + the X and Meta pixels (both no-op unless
  // their pixel/event IDs are configured, i.e. production paid traffic).
  // Gated mobile visitors are excluded so the paid-funnel metric only counts
  // visitors who actually saw the signup form.
  useEffect(() => {
    if (!gatedToApp) trackSignupPageView();
  }, [gatedToApp]);

  if (gatedToApp) {
    return <Navigate to="/get-the-app" replace />;
  }

  return (
    <div className="text-center">
      {refCode && !showGuestForm ? (
        <InvitedSignupForm onContinueAsGuest={() => setShowGuestForm(true)} />
      ) : (
        <GuestSignupForm />
      )}

      {/* What is Polymyth? A 30s watch converts unproven visitors faster than copy. */}
      {PROMO_VIDEO_ID && (
        <div className="mt-10 flex flex-col items-center gap-3">
          <h3 className="text-xl sm:text-2xl font-character text-foreground">
            New here? See Polymyth in half a minute
          </h3>
          <p className="max-w-md text-sm text-muted-foreground">
            Full 5E adventures, solo or with friends. No rules to learn, no Game Master to find.
            Build a hero and you are playing in under a minute.
          </p>
          <PromoVideo className="mt-2" />
        </div>
      )}

      {/* Mobile section */}
      <div
        className="mt-10 h-px w-full"
        style={{
          background:
            'linear-gradient(to right, transparent, hsl(var(--accent-gold) / 0.3), transparent)',
        }}
      />
      <div className="flex flex-col items-center gap-3 pt-8">
        <h3 className="text-xl sm:text-2xl font-character text-foreground">
          Play on phone or tablet
        </h3>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <AppStoreBadge />
          <PlayStoreBadge />
        </div>
      </div>
    </div>
  );
}

/** Real-account signup form shown to visitors arriving via a friend's `?ref=` link. */
function InvitedSignupForm({ onContinueAsGuest }: { onContinueAsGuest: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  const handleGoogleSignUp = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      // signInWithGoogle flips isLoggedIn; AuthLayout then reads this and
      // routes to /lobby. We don't navigate here; this component unmounts on
      // that redirect.
      sessionStorage.setItem('post-auth-redirect', '/lobby');
      await signInWithGoogle();
    } catch (err) {
      sessionStorage.removeItem('post-auth-redirect');
      setGoogleLoading(false);
      setError(
        err instanceof Error ? err.message : 'Failed to sign in with Google. Please try again.'
      );
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      sessionStorage.setItem('post-auth-redirect', '/lobby');
      await signUpWithEmail(email, password, undefined, turnstileToken ?? undefined);
    } catch (err) {
      sessionStorage.removeItem('post-auth-redirect');
      setLoading(false);
      // Turnstile tokens are single-use and the backend already consumed this one
      // (see GuestSignupForm's handleStart for the same reasoning).
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      setError(
        err instanceof Error ? err.message : 'Could not create your account. Please try again.'
      );
    }
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-foreground mb-2">A friend invited you</h2>
      <p className="text-muted-foreground mb-6">Sign up to claim your bonus tokens.</p>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/15 p-3 text-sm text-destructive border border-destructive/30">
          {error}
        </div>
      )}

      <Button
        type="button"
        onClick={handleGoogleSignUp}
        disabled={loading || googleLoading}
        variant="outline"
        className="w-full mb-3"
      >
        {googleLoading ? 'Connecting…' : 'Continue with Google'}
      </Button>

      <div className="relative mb-3">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">or</span>
        </div>
      </div>

      <form onSubmit={handleEmailSignUp} className="space-y-4">
        <Input
          type="email"
          name="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          type="password"
          name="new-password"
          autoComplete="new-password"
          enterKeyHint="go"
          placeholder="Password (6+ characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />
        {turnstileSiteKey && (
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            onToken={setTurnstileToken}
            onReset={() => setTurnstileToken(null)}
          />
        )}
        <Button
          type="submit"
          className="w-full uppercase tracking-wide bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
          disabled={loading || googleLoading || (!!turnstileSiteKey && !turnstileToken)}
        >
          {loading ? <AnkhSpinner size="sm" /> : 'Sign Up'}
        </Button>
      </form>

      <p className="mt-6 text-sm">
        <button
          type="button"
          onClick={onContinueAsGuest}
          className="text-muted-foreground hover:text-foreground underline"
        >
          Continue as guest instead
        </button>
      </p>

      <p className="mt-4 text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </>
  );
}

/** Guest-first signup form (unchanged default entry point). */
function GuestSignupForm() {
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const name = username.trim();
    if (!name) {
      setError('Please enter a username.');
      return;
    }
    // The lobby drops all-numeric names as anonymous-uid noise, so a name like
    // "123" would silently show the default lobby name. Require a letter.
    if (/^\d+$/.test(name)) {
      setError('Your username needs at least one letter.');
      return;
    }

    setLoading(true);
    try {
      // `startGuest` provisions the guest + flips isLoggedIn; AuthLayout then reads
      // this and routes to /lobby. We don't navigate here; this page unmounts on
      // that redirect. Leave the spinner up meanwhile.
      sessionStorage.setItem('post-auth-redirect', '/lobby');
      await startGuest(name, turnstileToken ?? undefined);
    } catch (err) {
      sessionStorage.removeItem('post-auth-redirect');
      setLoading(false);
      // Turnstile tokens are single-use and the backend already consumed this one.
      // Clear it and reset the widget so the next attempt mints a fresh token,
      // otherwise the submit button re-enables with a dead token and every retry
      // fails with "timeout-or-duplicate", bricking the signup funnel until expiry.
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      console.error('Guest start failed:', err);
      setError('Could not start your session. Please try again.');
    }
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-foreground mb-2">Start Playing</h2>
      <p className="text-muted-foreground mb-6">
        Pick a name and jump in. No email, no password. Save your account later.
      </p>

      <form onSubmit={handleStart} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive border border-destructive/30">
            {error}
          </div>
        )}
        <Input
          type="text"
          name="username"
          autoComplete="nickname"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          placeholder="Choose a username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          maxLength={30}
        />
        {turnstileSiteKey && (
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            onToken={setTurnstileToken}
            onReset={() => setTurnstileToken(null)}
          />
        )}
        <Button
          type="submit"
          className="w-full uppercase tracking-wide bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
          disabled={loading || (!!turnstileSiteKey && !turnstileToken)}
        >
          {loading ? <AnkhSpinner size="sm" /> : 'Start Playing'}
        </Button>
      </form>

      <p className="mt-6 text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </>
  );
}
